import React, { useEffect, useState, useCallback } from "react";
import LabTechnicianSidebar from "./Techniciansidebar";
import maintenanceService from "../services/maintenanceService";
import {
  FaWrench,
  FaClock,
  FaCheckCircle,
  FaCalendarAlt,
  FaSpinner,
  FaTimes,
  FaExclamationTriangle
} from "react-icons/fa";
import "../styles/TechnicianMaintenanceRequests.css";


const MaintenanceRequest = () => {

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [selectedRequest, setSelectedRequest] = useState(null);
  
  // Maintenance modal states
  const [completionNotes, setCompletionNotes] = useState('');
  const [rejectionNotes, setRejectionNotes] = useState('');
  const [proofFiles, setProofFiles] = useState([]);
  const [modalAction, setModalAction] = useState(null);
  const [progressImages, setProgressImages] = useState(null);

  // Fetch requests
  const fetchRequests = useCallback(async () => {
    try {
      setLoading(true);

      const response =
        await maintenanceService.getMyMaintenanceRequests(
          statusFilter || null
        );

      setRequests(response.data || []);
    } catch (error) {
      console.error("Error fetching maintenance requests", error);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  // Update status
  const updateStatus = async (id, status) => {
    try {
      setLoading(true);

      await maintenanceService.updateMaintenanceStatus(id, status);

      fetchRequests();
      setSelectedRequest(null);
    } catch (error) {
      console.error("Error updating status", error);
      alert("Error updating maintenance status");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return "—";
    return new Date(date).toLocaleDateString("en-IN");
  };

  const getStatusClass = (status) => {
    return `status-badge status-${status}`;
  };

  const getPriorityClass = (priority) => {
    return `priority-badge priority-${priority}`;
  };

  const handleProofFileUpload = (event) => {
    const files = Array.from(event.target.files);
    setProofFiles([...proofFiles, ...files]);
  };

  const handleRemoveProofFile = (index) => {
    setProofFiles(proofFiles.filter((_, i) => i !== index));
  };

  const handleProgressImageUpload = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      // Create a preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setProgressImages({
          file: file,
          preview: e.target?.result
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveProgressImage = () => {
    setProgressImages(null);
  };

  const handleCompleteMaintenance = async (id) => {
    try {
      setLoading(true);
      const updateData = {
        status: 'completed',
        completionNotes: completionNotes
      };

      if (proofFiles.length > 0) {
        const formData = new FormData();
        formData.append('status', 'completed');
        formData.append('completionNotes', completionNotes);
        proofFiles.forEach((file) => {
          formData.append('proofFiles', file);
        });

        await maintenanceService.updateMaintenanceStatusWithProof(id, formData);
      } else {
        await maintenanceService.updateMaintenanceStatus(id, updateData);
      }

      fetchRequests();
      setSelectedRequest(null);
      setCompletionNotes('');
      setProofFiles([]);
      setModalAction(null);
    } catch (error) {
      console.error('Error completing maintenance:', error);
      alert('Error completing maintenance');
    } finally {
      setLoading(false);
    }
  };

  const handleRejectMaintenance = async (id) => {
    try {
      setLoading(true);
      await maintenanceService.updateMaintenanceStatus(id, {
        status: 'rejected',
        rejectionReason: rejectionNotes
      });

      fetchRequests();
      setSelectedRequest(null);
      setRejectionNotes('');
      setModalAction(null);
    } catch (error) {
      console.error('Error rejecting maintenance:', error);
      alert('Error rejecting maintenance');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="technician-maintenance-page">
      <LabTechnicianSidebar user={null} />
      <div className="page-container">
        <div className="maintenance-page">

      <h2>
        <FaWrench /> Maintenance Requests
      </h2>

      {/* FILTER */}
      <div className="filter-box">
        <label>Status</label>

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="">All</option>
          <option value="pending">Pending</option>
          <option value="in_progress">In Progress</option>
          <option value="completed">Completed</option>
          <option value="rejected">Rejected</option>
        </select>
      </div>

      {/* SUMMARY CARDS */}

      <div className="summary-row">

        <div className="summary-card pending">
          <FaClock />
          <div>
            <span>{requests.filter(r => r.status === "pending").length}</span>
            <p>Pending</p>
          </div>
        </div>

        <div className="summary-card progress">
          <FaWrench />
          <div>
            <span>{requests.filter(r => r.status === "in_progress").length}</span>
            <p>In Progress</p>
          </div>
        </div>

        <div className="summary-card complete">
          <FaCheckCircle />
          <div>
            <span>{requests.filter(r => r.status === "completed").length}</span>
            <p>Completed</p>
          </div>
        </div>

        <div className="summary-card total">
          <FaCalendarAlt />
          <div>
            <span>{requests.length}</span>
            <p>Total</p>
          </div>
        </div>

      </div>

      {/* TABLE */}

      {loading && (
        <p className="loading">
          <FaSpinner className="spin" /> Loading...
        </p>
      )}

      {!loading && requests.length === 0 && (
        <p>No maintenance requests assigned</p>
      )}

      {!loading && requests.length > 0 && (

        <table className="maintenance-table">

          <thead>
            <tr>
              <th>Asset</th>
              <th>Department</th>
              <th>Type</th>
              <th>Priority</th>
              <th>Status</th>
              <th>Expected By</th>
              <th>Action</th>
            </tr>
          </thead>

          <tbody>

            {requests.map((req) => (

              <tr key={req._id}>

                <td>{req.assetName}</td>

                <td>{req.department}</td>

                <td>{req.maintenanceType}</td>

                <td>
                  <span className={getPriorityClass(req.priorityLevel)}>
                    {req.priorityLevel?.toUpperCase()}
                  </span>
                </td>

                <td>
                  <span className={getStatusClass(req.status)}>
                    {req.status.replace("_", " ").toUpperCase()}
                  </span>
                </td>

                <td>
                  {formatDate(req.expectedCompletionDate)}

                  {new Date(req.expectedCompletionDate) < new Date()
                    && req.status !== "completed" && (

                      <FaExclamationTriangle
                        className="overdue"
                        title="Overdue"
                      />

                  )}
                </td>

                <td>

                  <button
                    className="btn-view"
                    onClick={() => setSelectedRequest(req)}
                  >
                    View
                  </button>

                </td>

              </tr>

            ))}

          </tbody>

        </table>

      )}

      {/* MODAL */}

      {selectedRequest && (

        <div
          className="modal-overlay"
          onClick={() => setSelectedRequest(null)}
        >

          <div
            className="modal"
            onClick={(e) => e.stopPropagation()}
          >

            <div className="modal-header">
              <h3>
                <FaWrench style={{ marginRight: '10px' }} />
                Maintenance Request Details
              </h3>
              <button
                className="modal-close-btn"
                onClick={() => setSelectedRequest(null)}
              >
                <FaTimes />
              </button>
            </div>

            <div className="modal-body">

              {/* Status & Priority Badges */}
              <div className="modal-section full-width">
                <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
                  <span className={getStatusClass(selectedRequest.status)}>
                    {selectedRequest.status.replace('_', ' ').toUpperCase()}
                  </span>
                  <span className={getPriorityClass(selectedRequest.priorityLevel)}>
                    {selectedRequest.priorityLevel.toUpperCase()}
                  </span>
                  <span className="maintenance-type-badge">{selectedRequest.maintenanceType}</span>
                </div>
              </div>

              {/* Asset Information */}
              <div className="modal-info-card">
                <h4 className="modal-card-title">Asset Information</h4>
                <div className="modal-info-items">
                  <div className="modal-info-item">
                    <span className="info-label">Asset Name</span>
                    <span className="info-value">{selectedRequest.assetName}</span>
                  </div>
                  <div className="modal-info-item">
                    <span className="info-label">Serial Number</span>
                    <span className="info-value">{selectedRequest.serialNumber || 'N/A'}</span>
                  </div>
                  <div className="modal-info-item">
                    <span className="info-label">Department</span>
                    <span className="info-value">{selectedRequest.department}</span>
                  </div>
                </div>
              </div>

              {/* Dates Information */}
              <div className="modal-info-card">
                <h4 className="modal-card-title">Timeline</h4>
                <div className="modal-info-items">
                  <div className="modal-info-item">
                    <span className="info-label">Reported</span>
                    <span className="info-value">{new Date(selectedRequest.reportedDate).toLocaleString()}</span>
                  </div>
                  <div className="modal-info-item">
                    <span className="info-label">Expected Completion</span>
                    <span className="info-value">{new Date(selectedRequest.expectedCompletionDate).toLocaleString()}</span>
                  </div>
                  {selectedRequest.completedDate && (
                    <div className="modal-info-item">
                      <span className="info-label">Completed</span>
                      <span className="info-value">{new Date(selectedRequest.completedDate).toLocaleString()}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Description */}
              {selectedRequest.description && (
                <div className="modal-info-card">
                  <h4 className="modal-card-title">Description</h4>
                  <p className="modal-description">{selectedRequest.description}</p>
                </div>
              )}

              {/* Completion Notes */}
              {selectedRequest.completionNotes && (
                <div className="modal-info-card modal-completion-card">
                  <h4 className="modal-card-title">Completion Notes</h4>
                  <p className="modal-description">{selectedRequest.completionNotes}</p>
                </div>
              )}

              {/* Rejection Notes */}
              {selectedRequest.rejectionReason && (
                <div className="modal-info-card modal-rejection-card">
                  <h4 className="modal-card-title">Rejection Reason</h4>
                  <p className="modal-description">{selectedRequest.rejectionReason}</p>
                </div>
              )}

              {/* Attachments Section */}
              {selectedRequest.proofFiles && selectedRequest.proofFiles.length > 0 && (
                <div className="modal-attachments-section">
                  <h4>Proof of Work ({selectedRequest.proofFiles.length})</h4>
                  <div className="attachment-list">
                    {selectedRequest.proofFiles.map((file, idx) => (
                      <div key={idx} className="attachment-item">
                        <a href={file.url} download={file.originalName} target="_blank" rel="noopener noreferrer">
                          {file.originalName}
                        </a>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Progress Image Upload Section (for in_progress requests) */}
              {selectedRequest.status === "in_progress" && !modalAction && (
                <div className="modal-info-card modal-progress-card">
                  <h4 className="modal-card-title">Upload Progress Images</h4>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#475569' }}>
                    Upload images of current work progress
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleProgressImageUpload}
                    style={{
                      display: 'block',
                      padding: '10px',
                      border: '2px dashed #3b82f6',
                      borderRadius: '6px',
                      marginBottom: '10px',
                      width: '100%',
                      cursor: 'pointer',
                      boxSizing: 'border-box'
                    }}
                  />
                  {progressImages && (
                    <div style={{
                      marginTop: '12px',
                      padding: '12px',
                      backgroundColor: '#eff6ff',
                      borderRadius: '6px',
                      border: '1px solid #bfdbfe'
                    }}>
                      <div style={{
                        display: 'flex',
                        gap: '12px',
                        alignItems: 'flex-start'
                      }}>
                        <img
                          src={progressImages.preview}
                          alt="preview"
                          style={{
                            width: '80px',
                            height: '80px',
                            objectFit: 'cover',
                            borderRadius: '4px',
                            border: '1px solid #cbd5e1'
                          }}
                        />
                        <div style={{ flex: 1 }}>
                          <p style={{ margin: '0 0 8px 0', fontWeight: '600', color: '#1e293b' }}>
                            {progressImages.file.name}
                          </p>
                          <p style={{ margin: '0 0 8px 0', fontSize: '12px', color: '#64748b' }}>
                            {(progressImages.file.size / 1024).toFixed(2)} KB
                          </p>
                          <button
                            type="button"
                            onClick={handleRemoveProgressImage}
                            style={{
                              padding: '4px 12px',
                              backgroundColor: '#fecaca',
                              color: '#991b1b',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '12px',
                              fontWeight: '600'
                            }}
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Completion Form */}
              {modalAction === 'complete' && (
                <div className="action-form">
                  <h4>Complete Maintenance Request</h4>
                  <label style={{ display: 'block', marginTop: '12px', fontWeight: '600', marginBottom: '8px' }}>
                    Completion Notes (Required)
                  </label>
                  <textarea
                    value={completionNotes}
                    onChange={(e) => setCompletionNotes(e.target.value)}
                    placeholder="Describe what was done and current status"
                    rows="3"
                    required
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #cbd5e1',
                      borderRadius: '6px',
                      fontFamily: 'inherit',
                      resize: 'vertical'
                    }}
                  />

                  <label style={{ display: 'block', marginTop: '15px', fontWeight: '600', marginBottom: '8px' }}>
                    Upload Proof (Images/Videos) - Optional
                  </label>
                  <input
                    type="file"
                    multiple
                    accept="image/*,video/*"
                    onChange={handleProofFileUpload}
                    style={{
                      display: 'block',
                      padding: '8px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      marginBottom: '10px',
                      width: '100%'
                    }}
                  />

                  {proofFiles.length > 0 && (
                    <div style={{
                      marginTop: '10px',
                      padding: '10px',
                      backgroundColor: '#f0f4f8',
                      borderRadius: '4px'
                    }}>
                      <label style={{ fontWeight: '600', display: 'block', marginBottom: '8px' }}>
                        Selected Files: ({proofFiles.length})
                      </label>
                      {proofFiles.map((file, index) => (
                        <div
                          key={index}
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '8px',
                            backgroundColor: '#fff',
                            marginBottom: '6px',
                            borderRadius: '4px',
                            border: '1px solid #e0e0e0'
                          }}
                        >
                          <span style={{ fontSize: '13px' }}>
                            {file.name} ({(file.size / 1024).toFixed(2)} KB)
                          </span>
                          <button
                            type="button"
                            onClick={() => handleRemoveProofFile(index)}
                            style={{
                              padding: '4px 8px',
                              backgroundColor: '#ff6b6b',
                              color: 'white',
                              border: 'none',
                              borderRadius: '3px',
                              cursor: 'pointer',
                              fontSize: '12px'
                            }}
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="form-buttons">
                    <button
                      className="btn-submit"
                      onClick={() => handleCompleteMaintenance(selectedRequest._id)}
                      disabled={!completionNotes || loading}
                    >
                      Submit Completion
                    </button>
                    <button
                      className="btn-cancel"
                      onClick={() => {
                        setModalAction(null);
                        setProofFiles([]);
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* Rejection Form */}
              {modalAction === 'reject' && (
                <div className="action-form">
                  <h4>Reject Maintenance Request</h4>
                  <textarea
                    value={rejectionNotes}
                    onChange={(e) => setRejectionNotes(e.target.value)}
                    placeholder="Reason for rejection"
                    rows="3"
                    required
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #cbd5e1',
                      borderRadius: '6px',
                      fontFamily: 'inherit',
                      resize: 'vertical',
                      marginTop: '12px'
                    }}
                  />

                  <div className="form-buttons">
                    <button
                      className="btn-submit"
                      onClick={() => handleRejectMaintenance(selectedRequest._id)}
                      disabled={!rejectionNotes || loading}
                      style={{ background: '#ef4444' }}
                    >
                      Submit Rejection
                    </button>
                    <button
                      className="btn-cancel"
                      onClick={() => setModalAction(null)}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

            </div>

            {!modalAction && (
              <div className="modal-footer">
                {selectedRequest.status === "pending" && (
                  <>
                    <button
                      className="btn-accept"
                      onClick={() =>
                        updateStatus(selectedRequest._id, "in_progress")
                      }
                    >
                      Accept & Start
                    </button>
                    <button
                      className="btn-reject"
                      onClick={() => setModalAction('reject')}
                    >
                      Reject
                    </button>
                  </>
                )}

                {selectedRequest.status === "in_progress" && (
                  <button
                    className="btn-complete"
                    onClick={() => setModalAction('complete')}
                  >
                    Mark Completed
                  </button>
                )}

                <button
                  className="btn-close"
                  onClick={() => setSelectedRequest(null)}
                >
                  Close
                </button>
              </div>
            )}

          </div>

        </div>

      )}

        </div>
      </div>
    </div>
  );
};

export default MaintenanceRequest;